import { type ChangeEvent } from 'react';

import { DayPicker } from 'react-day-picker';
import type { Locale } from 'react-day-picker';
import { de } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';
import { LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { getCalendarClassNames, localeMap } from './config';
import { type CalendarProps } from './types';
import { DropdownMenu } from '../../input/DropdownMenu';
import { cn } from '../../utils/cn';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { DropdownNav } from './DropDownNav';
import { COMPONENTS_NAMESPACE } from '../../locales';

/**
 * react-day-picker's dropdown `onChange` is a native select change handler that only reads
 * `event.target.value`. Our {@link DropdownMenu} emits the selected value directly, so we
 * rebuild a minimal change event that exposes the value as a string on `target`.
 */
const createDropdownChangeEvent = (value: number): ChangeEvent<HTMLSelectElement> => {
    const target = { value: String(value) } as HTMLSelectElement;
    return {
        currentTarget: target,
        target,
    } as ChangeEvent<HTMLSelectElement>;
};

/**
 * A calendar component for date selection.
 *
 * @param mode - Selection mode: "single" for single date, "range" for date range.
 * @param selected - The currently selected date(s).
 * @param onSelect - Callback fired when a date is selected.
 * @param numberOfMonths - Number of months to display. Defaults to 1 for single, 2 for range.
 * @param disabled - Dates or date matchers to disable.
 * @param showDropdowns - When true, shows month/year as dropdown menus instead of labels.
 * @param popover - When true, renders the calendar inside a popover.
 * @param trigger - Element that triggers the popover. Required when popover is true.
 * @param open - Controlled open state for the popover.
 * @param onOpenChange - Callback when the popover open state changes.
 * @param popoverAlign - Alignment of the popover relative to the trigger. Defaults to 'start'.
 * @param popoverContentClassName - Additional CSS classes for the popover content.
 * @param className - Additional CSS classes for the root element.
 * @param ...props - Additional props passed to DayPicker.
 *
 * @example
 * // Single date selection (standalone)
 * <Calendar
 *   mode="single"
 *   selected={date}
 *   onSelect={setDate}
 * />
 *
 * @example
 * // Date range selection
 * <Calendar
 *   mode="range"
 *   selected={dateRange}
 *   onSelect={setDateRange}
 *   numberOfMonths={2}
 * />
 *
 * @example
 * // With dropdown menus for month/year
 * <Calendar
 *   mode="single"
 *   selected={date}
 *   onSelect={setDate}
 *   showDropdowns
 * />
 *
 * @example
 * // Inside a popover with custom trigger
 * <Calendar
 *   mode="single"
 *   selected={date}
 *   onSelect={setDate}
 *   popover
 *   trigger={<button>Select Date</button>}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * />
 */
export const Calendar = ({
    className,
    classNames,
    locale,
    onOpenChange,
    open,
    popover = false,
    popoverAlign = 'start',
    popoverContentClassName,
    showDropdowns = false,
    trigger,
    ...props
}: CalendarProps) => {
    const { i18n } = useTranslation(COMPONENTS_NAMESPACE);

    const numberOfMonths = props.numberOfMonths ?? (props.mode === 'range' ? 2 : 1);
    const showOutsideDays = props.showOutsideDays ?? true;

    // Resolve locale: use provided locale, or fall back to current i18n language
    const resolvedLocale: Locale = (() => {
        if (typeof locale === 'object') return locale;
        if (typeof locale === 'string' && localeMap[locale]) return localeMap[locale];
        return localeMap[i18n.language] ?? de;
    })();

    const calendarClassNames = getCalendarClassNames(classNames);

    const calendarElement = (
        <DayPicker
            {...props}
            captionLayout={showDropdowns ? 'dropdown' : 'label'}
            className={cn('p-3 bg-white rounded shadow-md font-roboto', className)}
            classNames={calendarClassNames}
            components={{
                Chevron: ({ orientation }) => {
                    if (orientation === 'down') {
                        return <LuChevronDown className="size-3" />;
                    }
                    const Icon = orientation === 'left' ? LuChevronLeft : LuChevronRight;
                    return <Icon className="size-4" />;
                },
                MonthsDropdown: ({ onChange, options, value }) => (
                    <DropdownMenu
                        options={options ?? []}
                        value={value as Array<string | number>}
                        onChange={(selectedValue) => {
                            onChange?.(createDropdownChangeEvent(selectedValue as number));
                        }}
                    ></DropdownMenu>
                ),
                YearsDropdown: ({ onChange, options, value }) => (
                    <DropdownMenu
                        options={options ?? []}
                        value={value as Array<string | number>}
                        onChange={(selectedValue) => {
                            onChange?.(createDropdownChangeEvent(selectedValue as number));
                        }}
                    ></DropdownMenu>
                ),
            }}
            locale={resolvedLocale}
            navLayout="around"
            numberOfMonths={numberOfMonths}
            showOutsideDays={showOutsideDays}
        />
    );

    if (popover && trigger) {
        return (
            <Popover open={open} onOpenChange={onOpenChange}>
                <PopoverTrigger asChild>{trigger}</PopoverTrigger>
                <PopoverContent
                    className={cn('w-auto p-0 border border-grey-dark', popoverContentClassName)}
                    align={popoverAlign}
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    {calendarElement}
                </PopoverContent>
            </Popover>
        );
    }

    return calendarElement;
};
